/**
 * FilterModule — BiquadFilterNode in the signal chain.
 *
 * Four classic topologies: lowpass, highpass, bandpass, notch. Cutoff runs
 * 20 Hz to 20 kHz with an exponential curve so the knob feels musical across
 * the whole audible range. Resonance (Q) peaks at 30 — enough to self-ring
 * on sharp edges without a full self-oscillation (the aggressive "Ruin"
 * filter that actually self-oscillates is a Phase 5 AudioWorklet).
 */

import type { AudioEngine } from '../AudioEngine';
import { EnumParameter, NumericParameter, type AnyParameter } from '../../params/Parameter';
import type { ModuleState, SynthModule } from './SynthModule';
import { mintId, readParamState, writeParamState } from './util';

export type FilterType = 'lowpass' | 'highpass' | 'bandpass' | 'notch';

const FILTER_TYPES = ['lowpass', 'highpass', 'bandpass', 'notch'] as const;

export class FilterModule implements SynthModule {
  readonly kind = 'signal' as const;
  readonly type = 'filter' as const;
  readonly displayName = 'FILT';
  readonly id: string;
  bypass = false;

  readonly filterType = new EnumParameter<FilterType>({
    name: 'Type',
    default: 'lowpass',
    options: FILTER_TYPES,
    formatter: (v) =>
      ({ lowpass: 'LP', highpass: 'HP', bandpass: 'BP', notch: 'NTC' })[v],
  });
  readonly cutoff = new NumericParameter({
    name: 'Cutoff',
    default: 2000,
    min: 20,
    max: 20000,
    curve: 'exp',
    unit: 'Hz',
    smoothingMs: 8,
    // Strong bias high — uniform rolls on an exp-mapped cutoff land sub-100 Hz
    // often and produce silent patches. Skew=5 keeps ~80% of rolls > 500 Hz.
    randomSkew: 5,
    formatter: (v) =>
      v >= 1000 ? `${(v / 1000).toFixed(2)}k` : `${v.toFixed(v < 100 ? 1 : 0)}`,
  });
  readonly resonance = new NumericParameter({
    name: 'Q',
    default: 1,
    min: 0.1,
    max: 30,
    curve: 'exp',
    smoothingMs: 20,
    // Bias low — high resonance on a random cutoff self-oscillates painfully.
    randomSkew: 0.3,
    formatter: (v) => v.toFixed(2),
  });

  readonly parameters: readonly AnyParameter[] = [
    this.filterType,
    this.cutoff,
    this.resonance,
  ];

  private filter: BiquadFilterNode | null = null;
  private readonly unsubs: Array<() => void> = [];
  private started = false;

  constructor(id?: string) {
    this.id = id ?? mintId('filt');
  }

  get input(): AudioNode | null {
    return this.filter;
  }
  get output(): AudioNode | null {
    return this.filter;
  }

  init(engine: AudioEngine): void {
    if (this.started) return;
    const ctx = engine.context;

    this.filter = ctx.createBiquadFilter();
    this.filter.type = this.filterType.value;
    this.filter.frequency.value = this.cutoff.value;
    this.filter.Q.value = this.resonance.value;

    this.cutoff.bindAudioParam(this.filter.frequency, ctx);
    this.resonance.bindAudioParam(this.filter.Q, ctx);

    this.unsubs.push(
      this.filterType.subscribe((v) => {
        if (this.filter) this.filter.type = v;
      }),
    );

    this.started = true;
  }

  dispose(): void {
    for (const u of this.unsubs) u();
    this.unsubs.length = 0;
    this.filter?.disconnect();
    this.filter = null;
    this.started = false;
  }

  serialize(): ModuleState {
    return {
      id: this.id,
      type: this.type,
      bypass: this.bypass,
      params: writeParamState(this.parameters),
    };
  }

  deserialize(state: ModuleState): void {
    if (state.bypass !== undefined) this.bypass = state.bypass;
    readParamState(this.parameters, state.params, 'preset');
  }
}
